// src/pages/ForgotPassword.tsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Area, Button } from '../styles/_app.style';
import {
  RegisterArea,
  FormArea,
  Form,
  Tagline,
  FormActions,
  Field,
  Label,
  Control,
  Message,
} from '../styles/login.style';
import { BookLogo } from '../components/common/BookLogo';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { useScale } from './HeightScaler';
import { baseURL } from '../constants/endpoints';
import axios from 'axios';
import { useToast } from '../contextManager/toast.context';

const ForgotPassword = () => {
  const { scale } = useScale();
  const { addToast, updateToast } = useToast();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('sending');
    const toastId = `forgot-password-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Sending reset link...' });
    try {
      await axios.post(`${baseURL}/password/forgot`, { email });
      setStatus('sent');
      updateToast(toastId, { variant: 'success', title: 'Reset link sent — check your inbox.' });
    } catch {
      setStatus('error');
      updateToast(toastId, { variant: 'error', title: 'Could not send reset link.' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password - tute</title>
      </Helmet>
      <RegisterArea>
        <AuthSkyline />
        <Area
          css={{
            height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
            marginTop: `calc(${NAVIGATION_HEIGHT})`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FormArea>
            <Form onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: 'max-content',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.8rem',
                    lineHeight: '1',
                    color: 'var(--colors-slate12, #1e293b)',
                    letterSpacing: '-0.5px',
                  }}
                >
                  tute
                </span>
                <div style={{ width: '100%', height: '16px', marginTop: '0.1rem' }}>
                  <BookLogo />
                </div>
              </div>

              <Tagline style={{ marginBottom: '0.5rem' }}>
                {status === 'sent'
                  ? 'Check your inbox'
                  : 'Reset your password'}
              </Tagline>

              {status === 'sent' ? (
                <p style={{ color: '#666', textAlign: 'center', margin: '1rem 0' }}>
                  If an account exists with that email, we've sent a reset link.
                  Check your inbox and spam folder.
                </p>
              ) : (
                <>
                  <p style={{ color: '#666', textAlign: 'center', margin: '0 0 1rem' }}>
                    Enter your email and we'll send you a link to reset your password.
                  </p>

                  <Field
                    name="email"
                    css={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}
                  >
                    <Label>Email</Label>
                    <Control
                      type="email"
                      required
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    />
                    <Message match="valueMissing">Please enter your email</Message>
                  </Field>

                  {status === 'error' && (
                    <p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center' }}>
                      Something went wrong. Please try again.
                    </p>
                  )}
                </>
              )}

              <FormActions>
                {status === 'sent' ? (
                  <Link to="/login">
                    <Button type="button" block tone="success">
                      Back to Login
                    </Button>
                  </Link>
                ) : (
                    <>
                  <Button
                    type="submit"
                    block
                    tone="success"
                    disabled={status === 'sending' || !email}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                <Link to="/login" style={{ marginTop: '1rem', display: 'inline-block' }}>
                Back to login
                </Link>
            </>
                )}


              </FormActions>
            </Form>
          </FormArea>
        </Area>
      </RegisterArea>
    </>
  );
};

export default ForgotPassword;